import React, { useEffect, useRef, useState } from "react";
import BottomSheet from "./BottomSheet";
import { PROFILE_ICONS } from "./profileMenuIcons";
import { usersApi, ReferralStats } from "../services/users";
import { copyTextToClipboard } from "../lib/clipboard";
import { formatAmountForCopy, formatMoneyByCurrency, formatTxnAmount } from "../utils/currency";
import "./ReferEarnSheet.css";
import { SHOW_TIERS } from "../lib/tiersPaused";
import { t, formatLocale } from "../i18n";
import { T } from "../i18n/T";
import { useReferralCashout } from "./ReferralCashout";
import { useFitText } from "../hooks/useFitText";
import type { ReferralCashout } from "../types/referrals";

// Refer & Earn sheet (Profile → Refer & Earn). Full screen since 1.5.2 b48
// (founder: "make it feel very professional and like you're gonna get paid",
// after Fanvue's Partners page):
//   • Total earned, big, on top; "Your referrals" under it, always shown (zeros
//     included, founder build 38) — tapping slides this same sheet over to the
//     list (name · joined · requests · what you earned from them).
//   • the code in one pill with Copy link (the round Share button went —
//     founder, b48); tap the code to change it, in place (founder 9/19).
//   • nothing earned yet: Fanvue's faint chart under the $0, with one true line
//     on it ("Your earnings will appear here").
//   • "Your terms": every rule of the deal spelled out, each one exactly what
//     lib/referrals.js on the server does — never a promise it doesn't keep.
//   • 1.5.2 (founder 10/22): that same tap also reveals "Add to my balance"
//     (ReferralCashout) on top of the list — never on the first screen. No
//     `cashout` block from the server = the list alone, exactly as before.
// Codes are not case sensitive; they are always SHOWN and TYPED in caps.

const SITE = "requesta.app";

export const referralLink = (code: string) => `https://${SITE}/join/${code.toLowerCase()}`;
// The message Share sends (founder-approved 10/08, 1.5.1 #2; replaces the
// 9/19 "Join with my code LUCKY" + link): one line of pitch, then the code
// and the link on their own line. The 💚 lives in the talent's message, not
// in our UI. The link previews as the sender's invite card (api/join-meta).
export const referralShareText = (code: string) => t("referEarnSheet.inviteMessage", { code: code.toUpperCase(), referralLink: referralLink(code) });

/**
 * The referral tier jump (founder 1.5.1 #11), from GET /orders/fee-tier:
 * `nextName` = the rung the jump lands them on (null at Legend — nothing to
 * jump to); `tierName` = their rung now (with the jump, once it's on).
 * Absent = tiers off / an older server → no card.
 */
export interface TierJump {
  approved: number;
  needed: number;
  active: boolean;
  nextName: string | null;
  tierName?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  stats: ReferralStats;
  onCodeChanged: (code: string) => void;
  tierJump?: TierJump | null;
  /** "Add to my balance" moved money (or the server said the feature is off): the fresh `cashout` block. */
  onCashoutChanged?: (next: ReferralCashout | undefined) => void;
  /** The add needs a bank first: open the money gate. */
  onConnectBank?: () => void;
  /** The success sheet's "See Balance". */
  onSeeBalance?: () => void;
}

const joinedOn = (iso: string) =>
  new Date(iso).toLocaleDateString(formatLocale(), { month: "short", day: "numeric" });

// Line icons for the sheet (no emoji in product UI), Brand Green via currentColor.
const svg = (d: React.ReactNode, size = 22, sw = 2) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
const TROPHY_ICON = svg(
  <>
    <path d="M8 4h8v5a4 4 0 01-8 0V4z" />
    <path d="M16 6h3a3 3 0 01-3 4M8 6H5a3 3 0 003 4" />
    <path d="M12 13v4M8.5 20h7M10 17h4v3h-4z" />
  </>,
  34,
  1.9
);
const LOCK_ICON = svg(<><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 018 0v3" /></>, 13, 2.4);
const CHECK_ICON = svg(<path d="M5 12.5l4.5 4.5L19 7.5" />, 13, 3);
const PEOPLE_ICON = svg(
  <>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19a5.5 5.5 0 0111 0" />
    <circle cx="17" cy="9" r="2.6" />
    <path d="M15.5 14.2A4.6 4.6 0 0121 19" />
  </>,
  24
);
const INFO_ICON = svg(<><circle cx="12" cy="12" r="9.5" /><path d="M12 11v5.5" /><path d="M12 7.6v.2" /></>, 20, 1.7);
const COPY_ICON = svg(<><rect x="8.5" y="8.5" width="11" height="11" rx="2.5" /><path d="M15.5 8.5V6.5a2 2 0 00-2-2h-7a2 2 0 00-2 2v7a2 2 0 002 2h2" /></>, 17, 2);
const PENCIL_ICON = svg(<><path d="M4 20h4L19 9l-4-4L4 16v4z" /><path d="M13.5 6.5l4 4" /></>, 14, 2.2);
const CHEVRON_UP = svg(<path d="M6 15l6-6 6 6" />, 18, 2.2);
const SPARKLE = <path d="M12 2c.6 4.6 2.8 7.4 7 8-4.2.6-6.4 3.4-7 8-.6-4.6-2.8-7.4-7-8 4.2-.6 6.4-3.4 7-8z" fill="currentColor" stroke="none" />;

// "Refer 5 talent → jump a tier" (founder 1.5.1 #11; the founder's 10/09
// mock-up puts it on top): the trophy, the rung it unlocks in the corner
// (locked until it's theirs), five bars that fill as the founder approves the
// talent they brought in; at five it reads "Tier jump unlocked." Hidden when
// there is no rung to jump to (Legend) and the jump isn't already theirs.
function TierJumpCard({ jump }: { jump: TierJump }) {
  const needed = Math.max(1, jump.needed);
  const approved = Math.max(0, Math.min(jump.approved, needed));
  if (!jump.active && !jump.nextName) return null;
  const pill = jump.active ? jump.tierName : jump.nextName;
  return (
    <div className={`re-jump${jump.active ? " is-on" : ""}`}>
      <span className="re-jump-spark re-jump-spark--1" aria-hidden="true">{svg(SPARKLE, 12)}</span>
      <span className="re-jump-spark re-jump-spark--2" aria-hidden="true">{svg(SPARKLE, 7)}</span>
      {pill && (
        <span className="re-jump-pill">
          {jump.active ? CHECK_ICON : LOCK_ICON}
          {pill}
        </span>
      )}
      <div className="re-jump-row">
        <span className="re-jump-ic" aria-hidden="true">{TROPHY_ICON}</span>
        <div className="re-jump-body">
          <div className="re-jump-h">{t("referEarnSheet.referTalentJumpA", { needed })}</div>
          <div className="re-jump-s">
            {jump.active
              ? t("referEarnSheet.tierJumpUnlocked")
              : t("referEarnSheet.ofApprovedMoreAnd", { approved, needed, count: needed - approved, name: jump.nextName || "" })}
          </div>
          <div className="re-jump-dots" aria-hidden="true">
            {Array.from({ length: needed }, (_, i) => (
              <span key={i} className={i < (jump.active ? needed : approved) ? "on" : undefined} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReferEarnSheet({ open, onClose, stats, onCodeChanged, tierJump = null, onCashoutChanged, onConnectBank, onSeeBalance }: Props) {
  const [view, setView] = useState<"code" | "people">("code");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [termsOpen, setTermsOpen] = useState(true);
  // Closed from outside (the success sheet's "See Balance"): open on the first screen next time.
  useEffect(() => {
    if (!open) setView("code");
  }, [open]);
  const cashout = useReferralCashout({
    open,
    cashout: stats.cashout,
    currency: stats.currency,
    onCashoutChanged,
    onConnectBank,
    onSeeBalance,
  });

  const code = stats.code.toUpperCase();
  // Nothing earned yet → a zero in the talent's own currency, never a blank.
  const earnedAny = Object.values(stats.earnedByCurrency || {}).some((v) => Number(v) > 0);
  const totalEarned = earnedAny
    ? formatMoneyByCurrency(stats.earnedByCurrency)
    : formatTxnAmount(0, stats.currency);
  // How much room the total needs (ReferEarnSheet.css): a long one drops the
  // icon and steps down, so an amount is never cut off.
  // The big number never wraps or gets cut: it shrinks to fit (a total in two
  // currencies runs long).
  const heroRef = useRef<HTMLSpanElement>(null);
  useFitText(heroRef, `${totalEarned}|${open}|${view}`, 22);
  // The code (up to 15 characters) keeps the pill's room: a long code on a
  // small phone turns "Copy link" into a round copy button, and the code
  // shrinks to fit before it would ever end in "…".
  const compactCopy = code.length > 11 && typeof window !== "undefined" && window.innerWidth < 360;
  const codeRef = useRef<HTMLSpanElement>(null);
  useFitText(codeRef, `${code}|${open}|${view}|${editing}`, 10);
  const close = () => {
    setView("code");
    setEditing(false);
    setError("");
    onClose();
  };

  const copy = async () => {
    await copyTextToClipboard(referralLink(code));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const save = async () => {
    const next = draft.trim().toLowerCase();
    if (!/^[a-z0-9]{4,15}$/.test(next)) {
      setError("4–15 letters or numbers");
      return;
    }
    setSaving(true);
    try {
      const res = await usersApi.setReferralCode(next);
      onCodeChanged(res.code);
      setEditing(false);
      setError("");
    } catch (e: any) {
      setError(e?.response?.data?.msg || t("referEarnSheet.thatDidnTSave"));
    } finally {
      setSaving(false);
    }
  };

  if (view === "people") {
    return (
      <>
      <BottomSheet open={open} title={t("referEarnSheet.myReferrals")} icon={PROFILE_ICONS.refer} onClose={close} tall>
        <div className="re-sheet">
          <button type="button" className="re-back" onClick={() => setView("code")}>
            {t("referEarnSheet.referEarn")}
          </button>
          {cashout.cards}
          <div className="re-total">
            <span>{t("referEarnSheet.joined", { joined: stats.joined })}</span>
            <b>{t("referEarnSheet.earned", { totalEarned })}</b>
          </div>
          {stats.people.length === 0 && <div className="re-empty">{t("referEarnSheet.noOneHasJoined")}</div>}
          {stats.people.map((p, i) => {
            const earned = formatMoneyByCurrency(p.earnedByCurrency);
            const nothingYet = Object.keys(p.earnedByCurrency || {}).length === 0;
            return (
              <div className="re-person" key={`${p.name}-${p.joinedAt}-${i}`}>
                <div className="re-person-main">
                  <div className="re-person-name">{p.name}</div>
                  <div className="re-person-sub">
                    {t("referEarnSheet.joinedRequests", { joinedOn: joinedOn(p.joinedAt), count: p.requests })}
                  </div>
                </div>
                <div className={`re-person-amt${nothingYet ? " is-zero" : ""}`}>{earned}</div>
              </div>
            );
          })}
        </div>
      </BottomSheet>
      {open && cashout.sheet}
      </>
    );
  }

  // Order (founder, 1.5.2 b48): the money, who joined, the code, the terms.
  return (
    <BottomSheet open={open} title={t("referEarnSheet.referEarn2")} icon={PROFILE_ICONS.refer} onClose={close} tall className="re-bsheet">
      <div className="re-sheet re-v2">
        {/* Hidden while Tier Level is paused (lib/tiersPaused). */}
        {SHOW_TIERS && tierJump && <TierJumpCard jump={tierJump} />}

        {/* Tapping the money opens the list too — where "Add to my balance"
            shows once it's switched on (founder 10/22: only after tapping
            Total earned). */}
        <button type="button" className="re-hero" onClick={() => setView("people")}>
          <span className="re-hero-k">{t("referEarnSheet.totalEarned")}</span>
          <span className="re-hero-v" ref={heroRef}>{totalEarned}</span>
        </button>

        {/* Nothing earned yet: Fanvue's faint line (founder, b48). Decoration
            only; the one line on it is true. */}
        {!earnedAny && (
          <div className="re-chart">
            <svg viewBox="0 0 340 120" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <linearGradient id="re-chart-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#ffffff" stopOpacity="0.05" />
                  <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M0 78 L34 80 L68 85 L101 87 L135 92 L169 90 L202 84 L236 78 L270 74 L303 34 L340 38 L340 120 L0 120 Z" fill="url(#re-chart-fill)" />
              <path d="M0 78 L34 80 L68 85 L101 87 L135 92 L169 90 L202 84 L236 78 L270 74 L303 34 L340 38" fill="none" stroke="#3a4252" strokeWidth="2" vectorEffect="non-scaling-stroke" />
            </svg>
            <span className="re-chart-pill">{t("referEarnSheet.earningsWillAppear")}</span>
          </div>
        )}

        {/* Always visible, zeros included (founder, build 38). Taps through to the list. */}
        <button type="button" className="re-refs" onClick={() => setView("people")}>
          <span className="re-refs-ic">{PEOPLE_ICON}</span>
          <span className="re-refs-k">{t("referEarnSheet.yourReferrals")}</span>
          <b className="re-refs-v">{stats.joined}</b>
          <span className="re-refs-chev" aria-hidden="true">›</span>
        </button>

        {editing ? (
          <div className="re-edit">
            <div className="re-code-lbl">{t("referEarnSheet.yourCode")}</div>
            <input
              className="re-code-input"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 15));
                setError("");
              }}
              aria-label={t("referEarnSheet.yourCode2")}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
            />
            <div className="re-code-link">
              <T k="referEarnSheet.join" vars={{ SITE, value: draft.toLowerCase() }} tags={{ 0: (c) => <b>{c}</b> }} />
            </div>
            <div className={`re-code-hint${error ? " bad" : ""}`} role={error ? "alert" : undefined}>
              {error || t("referEarnSheet.codeRules")}
            </div>
            <div className="re-edit-btns">
              <button type="button" className="re-mini re-mini--ghost" onClick={() => { setEditing(false); setError(""); }}>
                {t("referEarnSheet.cancel")}
              </button>
              <button type="button" className="re-mini" onClick={save} disabled={saving}>
                {saving ? t("referEarnSheet.saving") : t("referEarnSheet.save")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="re-share-row">
              <div className="re-pill">
                <button
                  type="button"
                  className="re-pill-code"
                  aria-label={t("referEarnSheet.yourCodeTapTo", { code })}
                  title={t("referEarnSheet.editCode")}
                  onClick={() => {
                    setDraft(code);
                    setEditing(true);
                  }}
                >
                  <span className="re-pill-code-txt" ref={codeRef}>{code}</span>
                  <span className="re-pill-pen">{PENCIL_ICON}</span>
                </button>
                <button
                  type="button"
                  className={`re-pill-copy${compactCopy ? " is-round" : ""}`}
                  onClick={copy}
                  aria-label={compactCopy ? (copied ? t("referEarnSheet.copied") : t("referEarnSheet.copyLink")) : undefined}
                >
                  {copied && compactCopy ? CHECK_ICON : COPY_ICON}
                  {!compactCopy && (copied ? t("referEarnSheet.copied") : t("referEarnSheet.copyLink"))}
                </button>
              </div>
            </div>
            <div className="re-code-link re-code-link--under">
              <T k="referEarnSheet.join" vars={{ SITE, value: code.toLowerCase() }} tags={{ 0: (c) => <b>{c}</b> }} />
            </div>
          </>
        )}

        {/* Every rule, spelled out (founder: "it makes it feel very
            professional"). Each line is what lib/referrals.js does: 2% of the
            base of every paid, played request; capped per referred talent in
            THEIR currency (capFor); paid from our fee; counted once 30 days old,
            so refunds and disputes have dropped out. */}
        <section className={`re-terms-card${termsOpen ? " is-open" : ""}`}>
          <button type="button" className="re-terms-head" onClick={() => setTermsOpen((v) => !v)} aria-expanded={termsOpen}>
            <span>{t("referEarnSheet.yourTerms")}</span>
            <span className="re-terms-chev" aria-hidden="true">{CHEVRON_UP}</span>
          </button>
          {termsOpen && (
            <ul className="re-terms-list">
              <li><span className="re-terms-ic">{INFO_ICON}</span><span><T k="referEarnSheet.term1" tags={{ 0: (c) => <b>{c}</b> }} /></span></li>
              <li><span className="re-terms-ic">{INFO_ICON}</span><span><T k="referEarnSheet.term2" vars={{ amount: formatAmountForCopy(stats.cap, stats.currency) }} tags={{ 0: (c) => <b>{c}</b> }} /></span></li>
              <li><span className="re-terms-ic">{INFO_ICON}</span><span><T k="referEarnSheet.term3" tags={{ 0: (c) => <b>{c}</b> }} /></span></li>
              <li><span className="re-terms-ic">{INFO_ICON}</span><span>{t("referEarnSheet.term4")}</span></li>
            </ul>
          )}
        </section>
      </div>
    </BottomSheet>
  );
}
